import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  ClipboardList,
  FileText,
  Folder,
  LayoutGrid,
  MessagesSquare,
  Plus,
  SendHorizontal,
  Target,
  Users,
  X,
} from "lucide-react";
import {
  AssistantWidthMenu,
  getAssistantWidthModeLabel,
} from "./AssistantWidthMenu";
import { getAssistantMotionTransition } from "./internal/assistantMotion";
import { schoolShellAssistantStyleContract } from "./internal/schoolShellAssistantStyleContract";
import styles from "../styles/AIAssistant.module.css";
import type {
  AssistantSceneEvents,
  AssistantSceneModel,
  AIAssistantPrototypePreviewPane,
  AIAssistantPrototypeSurface,
  AIAssistantPrototypeUtilityTab,
  AIAssistantPrototypeWidthMode,
} from "../types";

export type {
  AssistantSceneEvents,
  AssistantSceneModel,
  AIAssistantPrototypePreviewPane,
  AIAssistantPrototypeSurface,
  AIAssistantPrototypeUtilityTab,
  AIAssistantPrototypeWidthMode,
} from "../types";

export type AIAssistantPrototypeShellProps = {
  scene?: AssistantSceneModel;
  events?: AssistantSceneEvents;
  widthMode: AIAssistantPrototypeWidthMode;
  onWidthModeChange?: (mode: AIAssistantPrototypeWidthMode) => void;
  surface?: AIAssistantPrototypeSurface;
  onSurfaceChange?: (surface: AIAssistantPrototypeSurface) => void;
  onClose?: () => void;
  chatPane: React.ReactNode;
  filesPane: React.ReactNode;
  threadsPane: React.ReactNode;
  utilityTab?: AIAssistantPrototypeUtilityTab;
  onUtilityTabChange?: (tab: AIAssistantPrototypeUtilityTab) => void;
  utilityPaneOpen?: boolean;
  onUtilityPaneOpenChange?: (open: boolean) => void;
  previewPane?: AIAssistantPrototypePreviewPane | null;
  modelLabel?: string;
  modelMenu?: React.ReactNode;
  widthMenuOpen?: boolean;
  onWidthMenuOpenChange?: (open: boolean) => void;
  modelMenuOpen?: boolean;
  onModelMenuOpenChange?: (open: boolean) => void;
  onResetConversation?: () => void;
  showTitleBackButton?: boolean;
  onTitleBack?: () => void;
  onWelcomeSubmit?: (question: string) => boolean;
  assistantLabel?: string;
  workspaceLabel?: string;
  welcomeHeading?: string;
  welcomeMessage?: string;
  initialWelcomeVisible?: boolean;
  promptBanner?: string | null;
  fullPageChromeMode?: "default" | "body-only";
  className?: string;
};

const useMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener?.("change", sync);
    return () => media.removeEventListener?.("change", sync);
  }, []);

  return isMobile;
};

const resolveUtilityLabel = (tab: AIAssistantPrototypeUtilityTab): string =>
  tab === "files" ? "Files" : "Chats";

const AssistantTitleBackIcon = ({
  className,
  ...rest
}: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...rest}
  >
    <path d="M5 12h6m3 0h1.5m3 0h.5" />
    <path d="M5 12l6 6" />
    <path d="M5 12l6 -6" />
  </svg>
);

export function AIAssistantPrototypeShell({
  scene,
  events,
  widthMode,
  onWidthModeChange,
  surface,
  onSurfaceChange,
  onClose,
  chatPane,
  filesPane,
  threadsPane,
  utilityTab,
  onUtilityTabChange,
  utilityPaneOpen,
  onUtilityPaneOpenChange,
  previewPane,
  modelLabel = "GPT-4o",
  modelMenu,
  widthMenuOpen,
  onWidthMenuOpenChange,
  modelMenuOpen,
  onModelMenuOpenChange,
  onResetConversation,
  showTitleBackButton = false,
  onTitleBack,
  onWelcomeSubmit,
  assistantLabel = "Assistant",
  workspaceLabel = "Admissions",
  welcomeHeading = "Welcome!",
  welcomeMessage = "",
  initialWelcomeVisible = false,
  promptBanner = null,
  fullPageChromeMode = "default",
  className,
}: AIAssistantPrototypeShellProps) {
  const reducedMotion = Boolean(useReducedMotion());
  const isMobile = useMobileViewport();
  const [showWidthMenuState, setShowWidthMenuState] = useState(false);
  const [showModelMenuState, setShowModelMenuState] = useState(false);
  const [activeUtilityTabState, setActiveUtilityTabState] =
    useState<AIAssistantPrototypeUtilityTab>("files");
  const [showUtilityPaneState, setShowUtilityPaneState] = useState(false);
  const [showWelcomeViewState, setShowWelcomeViewState] = useState(
    initialWelcomeVisible,
  );
  const [welcomeQuestion, setWelcomeQuestion] = useState("");
  const widthMenuRef = useRef<HTMLDivElement | null>(null);
  const modelMenuRef = useRef<HTMLDivElement | null>(null);
  const modelMenuAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [modelMenuStyle, setModelMenuStyle] = useState<CSSProperties>({});
  const autoOpenedPreviewKeyRef = useRef<string | null>(null);
  const widthHoverCloseTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const sceneSurface = events?.onSurfaceChange ? scene?.surface : undefined;
  const sceneWidthMode = scene?.widthMode;
  const sceneUtilityTab = events?.onUtilityTabChange
    ? scene?.utilityTab
    : undefined;
  const sceneUtilityPaneOpen = events?.onUtilityPaneOpenChange
    ? scene?.utilityPaneOpen
    : undefined;
  const sceneWidthMenuOpen = scene?.widthMenuOpen;
  const sceneModelMenuOpen = scene?.modelMenuOpen;
  const scenePreviewPane =
    scene?.previewPane ??
    (scene?.workspace
      ? {
          title: scene.workspace.title,
          content: scene.workspace.content,
        }
      : null);
  const resolvedPreviewPane = scenePreviewPane ?? previewPane;
  const resolvedPreviewAutoOpenKey = resolvedPreviewPane
    ? resolvedPreviewPane.title
    : null;

  const resolvedWidthMode = sceneWidthMode ?? widthMode;
  const resolvedPanelVisibility = scene?.panelVisibility ?? "open";
  const resolvedSurface =
    sceneSurface ??
    surface ??
    (showWelcomeViewState
      ? "full-welcome"
      : resolvedWidthMode === "full" || isMobile
        ? "full-chat"
        : "rail-chat");
  const resolvedShowWelcomeView = resolvedSurface === "full-welcome";
  const resolvedIsFullScreen =
    isMobile || resolvedSurface !== "rail-chat" || resolvedWidthMode === "full";
  const resolvedShowWidthMenu =
    sceneWidthMenuOpen ?? widthMenuOpen ?? showWidthMenuState;
  const resolvedShowModelMenu =
    sceneModelMenuOpen ?? modelMenuOpen ?? showModelMenuState;
  const resolvedUtilityTab =
    sceneUtilityTab ?? utilityTab ?? activeUtilityTabState;
  const resolvedShowUtilityPane =
    sceneUtilityPaneOpen ?? utilityPaneOpen ?? showUtilityPaneState;
  const resolvedMobileSideSurface = resolvedPreviewPane
    ? "full-preview"
    : resolvedSurface === "full-files" ||
        resolvedSurface === "full-threads" ||
        resolvedSurface === "full-preview"
      ? resolvedSurface
      : null;
  const utilityPaneVisible = isMobile
    ? resolvedMobileSideSurface !== null
    : resolvedShowUtilityPane;
  const useBodyOnlyFullPageChrome =
    !isMobile && resolvedIsFullScreen && fullPageChromeMode === "body-only";
  const showDesktopWorkspaceChrome =
    !isMobile && resolvedIsFullScreen && !useBodyOnlyFullPageChrome;
  const shouldSplitPanel =
    !isMobile &&
    resolvedIsFullScreen &&
    resolvedShowUtilityPane &&
    !resolvedShowWelcomeView;
  const useClickWidthMenuTrigger = resolvedIsFullScreen && !isMobile;
  const showWidthMenuControl = resolvedIsFullScreen || resolvedShowWidthMenu;
  const usesPreviewSplit = shouldSplitPanel && Boolean(resolvedPreviewPane);
  const showChatPane = resolvedShowWelcomeView
    ? false
    : isMobile
      ? resolvedSurface === "rail-chat" || resolvedSurface === "full-chat"
      : !resolvedShowUtilityPane || shouldSplitPanel;
  const showSidePane = resolvedShowWelcomeView
    ? false
    : isMobile
      ? resolvedMobileSideSurface !== null
      : resolvedShowUtilityPane;
  const widthLabel = getAssistantWidthModeLabel(resolvedWidthMode);
  const activeUtilityLabel = resolveUtilityLabel(resolvedUtilityTab);
  const utilityPaneContent =
    resolvedUtilityTab === "files" ? filesPane : threadsPane;
  const showUtilityToggle = true;
  const resolvedPromptBanner =
    typeof promptBanner === "string" ? promptBanner.trim() : "";
  const splitChatPaneClassName = usesPreviewSplit ? "w-[38%]" : "w-[60%]";
  const splitSidePaneClassName = usesPreviewSplit ? "w-[62%]" : "w-[40%]";

  if (resolvedPanelVisibility === "closed") {
    return (
      <div
        className="fixed bottom-5 right-5 z-[1105] flex items-end justify-end"
        data-testid="ai-prototype-shell"
        data-width-mode={resolvedWidthMode}
        data-utility-tab={resolvedUtilityTab}
        data-surface={resolvedSurface}
        data-conversation-phase={scene?.conversationPhase ?? "idle"}
        data-workspace-kind={scene?.workspace?.kind ?? "chat"}
        data-ai-prototype-theme-surface="shell"
      >
        <button
          type="button"
          className={schoolShellAssistantStyleContract.floatingActionButtonClassName}
          aria-label={`Open ${assistantLabel}`}
          data-testid="ai-prototype-floating-action-button"
        >
          <Brain size={22} />
        </button>
      </div>
    );
  }

  const setResolvedSurface = (nextSurface: AIAssistantPrototypeSurface) => {
    events?.onSurfaceChange?.(nextSurface);
    onSurfaceChange?.(nextSurface);
    if (sceneSurface === undefined && surface === undefined) {
      setShowWelcomeViewState(nextSurface === "full-welcome");
    }
  };

  const setResolvedUtilityTab = (nextTab: AIAssistantPrototypeUtilityTab) => {
    events?.onUtilityTabChange?.(nextTab);
    onUtilityTabChange?.(nextTab);
    if (sceneUtilityTab === undefined && utilityTab === undefined) {
      setActiveUtilityTabState(nextTab);
    }
  };

  const setResolvedUtilityPaneOpen = (nextOpen: boolean) => {
    events?.onUtilityPaneOpenChange?.(nextOpen);
    onUtilityPaneOpenChange?.(nextOpen);
    if (sceneUtilityPaneOpen === undefined && utilityPaneOpen === undefined) {
      setShowUtilityPaneState(nextOpen);
    }
  };

  const setResolvedWidthMenuOpen = (nextOpen: boolean) => {
    events?.onWidthMenuOpenChange?.(nextOpen);
    onWidthMenuOpenChange?.(nextOpen);
    if (sceneWidthMenuOpen === undefined && widthMenuOpen === undefined) {
      setShowWidthMenuState(nextOpen);
    }
  };

  const setResolvedModelMenuOpen = (nextOpen: boolean) => {
    events?.onModelMenuOpenChange?.(nextOpen);
    onModelMenuOpenChange?.(nextOpen);
    if (sceneModelMenuOpen === undefined && modelMenuOpen === undefined) {
      setShowModelMenuState(nextOpen);
    }
  };

  const handleResetToQuickActions = () => {
    events?.onResetConversation?.();
    onResetConversation?.();
    setResolvedSurface(resolvedIsFullScreen ? "full-chat" : "rail-chat");
    setResolvedUtilityPaneOpen(false);
  };

  const handleTitleButtonClick = () => {
    if (showTitleBackButton) {
      onTitleBack?.();
      return;
    }
    handleResetToQuickActions();
  };

  const handlePreviewBack = () => {
    resolvedPreviewPane?.onBack?.();
    setResolvedSurface(resolvedIsFullScreen ? "full-chat" : "rail-chat");
    setResolvedUtilityPaneOpen(false);
  };

  useEffect(() => {
    if (!resolvedPreviewAutoOpenKey) {
      autoOpenedPreviewKeyRef.current = null;
      return;
    }
    if (autoOpenedPreviewKeyRef.current === resolvedPreviewAutoOpenKey) {
      return;
    }
    autoOpenedPreviewKeyRef.current = resolvedPreviewAutoOpenKey;
    if (resolvedUtilityTab !== "files") {
      setResolvedUtilityTab("files");
    }
    if (!resolvedShowUtilityPane) {
      setResolvedUtilityPaneOpen(true);
    }
    if (isMobile && resolvedSurface !== "full-preview") {
      setResolvedSurface("full-preview");
    }
  }, [
    isMobile,
    resolvedPreviewAutoOpenKey,
    resolvedShowUtilityPane,
    resolvedSurface,
    resolvedUtilityTab,
  ]);

  useEffect(() => {
    if (
      sceneSurface === undefined &&
      surface === undefined &&
      resolvedWidthMode !== "full" &&
      !isMobile
    ) {
      setShowWelcomeViewState(false);
    }
  }, [isMobile, resolvedWidthMode, sceneSurface, surface]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        widthMenuRef.current &&
        target &&
        !widthMenuRef.current.contains(target)
      ) {
        setResolvedWidthMenuOpen(false);
      }
      if (
        modelMenuRef.current &&
        target &&
        !modelMenuRef.current.contains(target)
      ) {
        setResolvedModelMenuOpen(false);
      }
    };

    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [widthMenuOpen, modelMenuOpen]);

  useLayoutEffect(() => {
    if (!resolvedShowModelMenu || typeof window === "undefined") {
      return undefined;
    }

    const viewportPadding = 12;
    const menuGap = 8;
    const preferredMenuWidth = 280;
    const preferredMenuHeight = 520;

    const updateModelMenuStyle = () => {
      const anchorRect = modelMenuAnchorRef.current?.getBoundingClientRect();
      const wrapperRect = modelMenuRef.current?.getBoundingClientRect();
      if (!anchorRect || !wrapperRect) {
        return;
      }

      const menuWidth = Math.min(
        preferredMenuWidth,
        Math.max(0, window.innerWidth - viewportPadding * 2),
      );
      const left = Math.min(
        Math.max(viewportPadding, anchorRect.right - menuWidth),
        Math.max(
          viewportPadding,
          window.innerWidth - viewportPadding - menuWidth,
        ),
      );
      const availableBelow = Math.max(
        0,
        Math.floor(
          window.innerHeight - anchorRect.bottom - menuGap - viewportPadding,
        ),
      );
      const availableAbove = Math.max(
        0,
        Math.floor(anchorRect.top - menuGap - viewportPadding),
      );
      const opensAbove =
        availableBelow < Math.min(preferredMenuHeight, 240) &&
        availableAbove > availableBelow;
      const constrainedMaxHeight = Math.min(
        preferredMenuHeight,
        opensAbove ? availableAbove : availableBelow,
      );
      const top = opensAbove
        ? Math.max(
            viewportPadding,
            anchorRect.top - menuGap - constrainedMaxHeight,
          )
        : Math.min(
            anchorRect.bottom + menuGap,
            Math.max(
              viewportPadding,
              window.innerHeight - viewportPadding - constrainedMaxHeight,
            ),
          );

      setModelMenuStyle({
        position: "absolute",
        left: `${left - wrapperRect.left}px`,
        right: "auto",
        top: `${top - wrapperRect.top}px`,
        bottom: "auto",
        width: `${menuWidth}px`,
        maxHeight: `${constrainedMaxHeight}px`,
      });
    };

    updateModelMenuStyle();
    window.addEventListener("resize", updateModelMenuStyle);
    window.addEventListener("scroll", updateModelMenuStyle, {
      passive: true,
    });

    return () => {
      window.removeEventListener("resize", updateModelMenuStyle);
      window.removeEventListener("scroll", updateModelMenuStyle);
    };
  }, [resolvedShowModelMenu]);

  useEffect(
    () => () => {
      if (widthHoverCloseTimeoutRef.current) {
        clearTimeout(widthHoverCloseTimeoutRef.current);
      }
    },
    [],
  );

  const cancelWidthHoverClose = () => {
    if (widthHoverCloseTimeoutRef.current) {
      clearTimeout(widthHoverCloseTimeoutRef.current);
      widthHoverCloseTimeoutRef.current = null;
    }
  };

  const openWidthMenu = () => {
    cancelWidthHoverClose();
    setResolvedModelMenuOpen(false);
    setResolvedWidthMenuOpen(true);
  };

  const closeWidthMenu = () => {
    cancelWidthHoverClose();
    setResolvedWidthMenuOpen(false);
  };

  const scheduleWidthHoverClose = () => {
    if (isMobile) {
      return;
    }
    closeWidthMenu();
  };

  const handleWidthHoverOpen = () => {
    if (isMobile) {
      return;
    }
    openWidthMenu();
  };

  const handleWidthButtonClick = () => {
    if (!isMobile && (events?.onClose || onClose)) {
      closeWidthMenu();
      events?.onClose?.();
      onClose?.();
      return;
    }
    if (isMobile || useClickWidthMenuTrigger) {
      if (resolvedShowWidthMenu) {
        closeWidthMenu();
        return;
      }
      openWidthMenu();
    }
  };

  const handleWidthChange = (nextMode: AIAssistantPrototypeWidthMode) => {
    events?.onWidthModeChange?.(nextMode);
    onWidthModeChange?.(nextMode);
    closeWidthMenu();
  };

  const handleWelcomeSubmit = () => {
    const trimmed = welcomeQuestion.trim();
    if (!trimmed) {
      return;
    }
    const handled =
      events?.onWelcomeSubmit?.(trimmed) ?? onWelcomeSubmit?.(trimmed) ?? false;
    if (handled) {
      setWelcomeQuestion("");
      if (resolvedWidthMode !== "full" && !isMobile) {
        events?.onWidthModeChange?.("full");
        onWidthModeChange?.("full");
      }
      setResolvedSurface("full-chat");
      setResolvedUtilityPaneOpen(false);
    }
  };

  const handleSidebarSelection = (
    target: "welcome" | "chat" | "files" | "threads",
  ) => {
    if (target === "welcome") {
      setResolvedSurface("full-welcome");
      setResolvedUtilityPaneOpen(false);
      return;
    }

    setResolvedSurface(resolvedIsFullScreen ? "full-chat" : "rail-chat");
    if (target === "chat") {
      setResolvedUtilityPaneOpen(false);
      return;
    }

    setResolvedUtilityTab(target);
    setResolvedUtilityPaneOpen(true);
    if (isMobile && resolvedIsFullScreen) {
      setResolvedSurface(target === "files" ? "full-files" : "full-threads");
    }
  };

  const utilityPaneNode = resolvedPreviewPane ? (
    <div
      className={schoolShellAssistantStyleContract.previewPaneClassName}
      data-testid="ai-prototype-preview-pane"
      data-ai-prototype-theme-surface="preview-pane"
    >
      <div
        className={schoolShellAssistantStyleContract.previewHeaderClassName}
        data-ai-prototype-theme-surface="preview-header"
      >
        <button
          type="button"
          onClick={handlePreviewBack}
          className={schoolShellAssistantStyleContract.previewBackButtonClassName}
          aria-label="Back to quick actions"
          data-testid="ai-prototype-preview-back"
        >
          <ArrowLeft size={14} />
        </button>
        <span className={schoolShellAssistantStyleContract.previewTitleClassName}>
          {resolvedPreviewPane.title}
        </span>
      </div>
      <div
        className={schoolShellAssistantStyleContract.previewBodyClassName}
        data-ai-prototype-theme-surface="preview-body"
      >
        {resolvedPreviewPane.content}
      </div>
    </div>
  ) : (
    <div
      className={schoolShellAssistantStyleContract.utilityPaneClassName}
      data-testid="ai-prototype-utility-pane"
      data-ai-prototype-pane-kind="recents"
      data-ai-prototype-theme-surface="utility-pane"
    >
      <div
        className={schoolShellAssistantStyleContract.utilityTabBarClassName}
        data-testid="ai-prototype-recents-tab-bar"
        data-ai-prototype-theme-surface="utility-tab-bar"
      >
        {(["files", "threads"] as const).map((tab) => {
          const active = resolvedUtilityTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setResolvedUtilityTab(tab)}
              className={schoolShellAssistantStyleContract.utilityTabClassName(active)}
              data-testid={`ai-prototype-utility-tab-${tab}`}
              data-ai-prototype-theme-surface="utility-tab"
              data-ai-prototype-utility-tab={tab}
              data-ai-prototype-tab-state={active ? "active" : "inactive"}
            >
              {tab === "files" ? (
                <FileText
                  size={14}
                  strokeWidth={1.8}
                  className="shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <MessagesSquare
                  size={14}
                  strokeWidth={1.8}
                  className="shrink-0"
                  aria-hidden="true"
                />
              )}
              <span className="text-center">{resolveUtilityLabel(tab)}</span>
            </button>
          );
        })}
      </div>
      <div
        className={schoolShellAssistantStyleContract.utilityTrayClassName}
        data-testid="ai-prototype-recents-tray"
        data-ai-prototype-theme-surface="utility-tray"
      >
        {utilityPaneContent}
      </div>
    </div>
  );

  const shouldShowBackdrop = resolvedShowModelMenu;
  const backdropNode = shouldShowBackdrop ? (
    <button
      type="button"
      className="absolute inset-0 z-[55] bg-irongrey/10 backdrop-blur-[1px]"
      aria-label="Dismiss assistant overlay surface"
      data-testid="ai-assistant-backdrop"
      onClick={() => {
        setResolvedModelMenuOpen(false);
        setResolvedWidthMenuOpen(false);
      }}
    />
  ) : null;

  const widthMenuNode = showWidthMenuControl ? (
    <AssistantWidthMenu
      currentMode={resolvedWidthMode}
      onModeSelect={handleWidthChange}
      onClose={
        events?.onClose || onClose
          ? () => {
              closeWidthMenu();
              events?.onClose?.();
              onClose?.();
            }
          : undefined
      }
      menuTestId="ai-prototype-width-menu"
      optionTestIdPrefix="ai-prototype-width-option"
      closeTestId="ai-prototype-width-close"
      align="left"
      open={resolvedShowWidthMenu}
    />
  ) : null;

  const modelMenuNode =
    resolvedShowModelMenu && modelMenu ? (
      <motion.div
        className={schoolShellAssistantStyleContract.modelMenuClassName}
        style={modelMenuStyle}
        initial={reducedMotion ? false : { opacity: 0, y: 6 }}
        animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
        transition={getAssistantMotionTransition("panel", reducedMotion)}
        data-testid="ai-prototype-model-menu"
        onClick={(event) => {
          const target = event.target as HTMLElement | null;
          if (
            target?.closest('[data-ai-model-option="true"]') ||
            target?.closest('[data-ai-context-menu-dismiss="true"]')
          ) {
            setResolvedModelMenuOpen(false);
          }
        }}
      >
        {modelMenu}
      </motion.div>
    ) : null;

  const headerNode = (
    <div
      className={schoolShellAssistantStyleContract.headerClassName}
      data-testid="ai-prototype-shell-header"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {showWidthMenuControl ? (
          <div
            className="relative"
            ref={widthMenuRef}
            onPointerEnter={handleWidthHoverOpen}
            onPointerLeave={scheduleWidthHoverClose}
            onFocusCapture={openWidthMenu}
            onBlurCapture={(event) => {
              const nextFocusedNode = event.relatedTarget as Node | null;
              if (
                widthMenuRef.current &&
                nextFocusedNode &&
                widthMenuRef.current.contains(nextFocusedNode)
              ) {
                return;
              }
              scheduleWidthHoverClose();
            }}
            onKeyDownCapture={(event) => {
              if (event.key === "Escape") {
                closeWidthMenu();
              }
            }}
          >
            <button
              type="button"
              onClick={handleWidthButtonClick}
              className={schoolShellAssistantStyleContract.headerSmallIconButtonClassName}
              aria-label={`Assistant width: ${widthLabel}`}
              aria-expanded={resolvedShowWidthMenu}
              aria-haspopup="menu"
              data-testid="ai-prototype-width-button"
            >
              <Brain size={16} />
            </button>
            {widthMenuNode}
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleTitleButtonClick}
          className={schoolShellAssistantStyleContract.headerTitleButtonClassName}
          data-testid="ai-prototype-shell-title"
        >
          {showTitleBackButton ? (
            <AssistantTitleBackIcon
              width={18}
              height={18}
              aria-hidden="true"
              focusable="false"
              className={schoolShellAssistantStyleContract.headerTitleBackIconClassName}
            />
          ) : null}
          <span>{assistantLabel}</span>
        </button>
      </div>

      <div className="flex items-center gap-1">
        {onResetConversation || events?.onResetConversation ? (
          <button
            type="button"
            onClick={handleResetToQuickActions}
            className={schoolShellAssistantStyleContract.headerIconButtonClassName()}
            aria-label={resolvedPreviewPane ? "Back to quick actions" : "New conversation"}
            title={resolvedPreviewPane ? "Back to quick actions" : "New conversation"}
            data-testid={resolvedPreviewPane ? "ai-prototype-back-to-quick-actions" : "ai-prototype-new-conversation"}
          >
            {resolvedPreviewPane ? <ArrowLeft size={17} /> : <Plus size={17} />}
          </button>
        ) : null}
        <div className="relative" ref={modelMenuRef}>
          <button
            type="button"
            ref={modelMenuAnchorRef}
            onClick={() => {
              setResolvedWidthMenuOpen(false);
              setResolvedModelMenuOpen(!resolvedShowModelMenu);
            }}
            className={schoolShellAssistantStyleContract.headerIconButtonClassName({
              active: resolvedShowModelMenu,
            })}
            aria-label={`Model: ${modelLabel}`}
            aria-expanded={resolvedShowModelMenu}
            aria-haspopup="menu"
            data-testid="ai-prototype-model-button"
          >
            <Target size={17} />
          </button>
          <AnimatePresence initial={false}>{modelMenuNode}</AnimatePresence>
        </div>
        {showUtilityToggle ? (
          <button
            type="button"
            onClick={() => {
              const next = !utilityPaneVisible;
              if (isMobile && resolvedIsFullScreen) {
                if (next) {
                  setResolvedSurface(
                    resolvedPreviewPane
                      ? "full-preview"
                      : resolvedUtilityTab === "threads"
                        ? "full-threads"
                        : "full-files",
                  );
                } else {
                  setResolvedSurface("full-chat");
                }
                setResolvedUtilityPaneOpen(next);
                return;
              }
              if (next) {
                setResolvedSurface(
                  resolvedIsFullScreen ? "full-chat" : "rail-chat",
                );
              }
              setResolvedUtilityPaneOpen(next);
            }}
            className={schoolShellAssistantStyleContract.headerIconButtonClassName({
              active: utilityPaneVisible,
            })}
            aria-label={
              utilityPaneVisible
                ? `Hide ${activeUtilityLabel}`
                : `Show ${activeUtilityLabel}`
            }
            aria-expanded={utilityPaneVisible}
            data-testid="ai-prototype-utility-toggle"
          >
            <ClipboardList size={17} />
          </button>
        ) : null}
        {!isMobile && (events?.onClose || onClose) ? (
          <button
            type="button"
            onClick={() => {
              events?.onClose?.();
              onClose?.();
            }}
            className={schoolShellAssistantStyleContract.headerIconButtonClassName()}
            aria-label="Close full assistant"
            data-testid="ai-prototype-shell-dismiss-full"
          >
            <X size={18} />
          </button>
        ) : null}
        {isMobile && (events?.onClose || onClose) ? (
          <button
            type="button"
            onClick={() => {
              events?.onClose?.();
              onClose?.();
            }}
            className={schoolShellAssistantStyleContract.headerIconButtonClassName()}
            aria-label="Close assistant"
            data-testid="ai-prototype-shell-dismiss-mobile"
          >
            <X size={18} />
          </button>
        ) : null}
      </div>
    </div>
  );

  const fullSidebarNode = showDesktopWorkspaceChrome ? (
    <div
      className={`${styles.nodisplay} ${schoolShellAssistantStyleContract.fullSidebarClassName}`}
      data-testid="ai-prototype-full-sidebar"
    >
      <div className={schoolShellAssistantStyleContract.fullSidebarBrandClassName}>
        I
      </div>
      <button
        type="button"
        onClick={() => handleSidebarSelection("welcome")}
        className={schoolShellAssistantStyleContract.fullSidebarButtonClassName({
          active: resolvedShowWelcomeView,
        })}
        aria-label="Assistant home"
        data-testid="ai-prototype-sidebar-home"
      >
        <Brain size={18} />
      </button>
      <button
        type="button"
        onClick={() => handleSidebarSelection("chat")}
        className={schoolShellAssistantStyleContract.fullSidebarButtonClassName({
          active: !resolvedShowWelcomeView && !resolvedShowUtilityPane,
        })}
        aria-label={`${workspaceLabel} workspace`}
        data-testid="ai-prototype-sidebar-workspace"
      >
        <LayoutGrid size={18} />
      </button>
      <button
        type="button"
        onClick={() => handleSidebarSelection("files")}
        className={schoolShellAssistantStyleContract.fullSidebarButtonClassName({
          active: resolvedShowUtilityPane && resolvedUtilityTab === "files",
        })}
        aria-label="Files"
        data-testid="ai-prototype-sidebar-files"
      >
        <Folder size={18} />
      </button>
      <button
        type="button"
        onClick={() => handleSidebarSelection("threads")}
        className={schoolShellAssistantStyleContract.fullSidebarButtonClassName({
          active: resolvedShowUtilityPane && resolvedUtilityTab === "threads",
        })}
        aria-label="Chats"
        data-testid="ai-prototype-sidebar-threads"
      >
        <Users size={18} />
      </button>
      <div className="flex-1" />
    </div>
  ) : null;

  const welcomeViewNode = (
    <motion.div
      className={schoolShellAssistantStyleContract.welcomeViewClassName}
      initial={reducedMotion ? false : { opacity: 0, y: 8 }}
      animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
      transition={getAssistantMotionTransition("panel", reducedMotion)}
      data-testid="ai-prototype-welcome-view"
      data-ai-prototype-theme-surface="welcome-view"
    >
      <div className={schoolShellAssistantStyleContract.welcomeIconClassName}>
        <Brain size={32} />
      </div>
      <h2
        className={schoolShellAssistantStyleContract.welcomeHeadingClassName}
      >
        {welcomeHeading}
      </h2>
      {welcomeMessage.trim().length > 0 ? (
        <p className={schoolShellAssistantStyleContract.welcomeMessageClassName}>
          {welcomeMessage}
        </p>
      ) : null}
      <div className="w-full max-w-[620px]">
        <div className={schoolShellAssistantStyleContract.welcomeInputShellClassName}>
          <textarea
            className={schoolShellAssistantStyleContract.welcomeInputClassName}
            placeholder="What would you like to take care of today?"
            value={welcomeQuestion}
            onChange={(event) => setWelcomeQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleWelcomeSubmit();
              }
            }}
            style={{ scrollbarWidth: "none" }}
            data-testid="ai-prototype-welcome-input"
          />
          <div className="flex items-center justify-between px-3 pb-3">
            <button
              type="button"
              className={schoolShellAssistantStyleContract.welcomeAttachClassName}
              aria-label="Add file"
              data-testid="ai-prototype-welcome-attach"
            >
              <Plus size={16} />
            </button>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setResolvedModelMenuOpen(!resolvedShowModelMenu)}
                className={schoolShellAssistantStyleContract.headerIconButtonClassName({
                  active: resolvedShowModelMenu,
                })}
                aria-label={`Model: ${modelLabel}`}
                data-testid="ai-prototype-welcome-model-button"
              >
                <Target size={17} />
              </button>
              <button
                type="button"
                onClick={handleWelcomeSubmit}
                disabled={welcomeQuestion.trim().length === 0}
                className={schoolShellAssistantStyleContract.welcomeSubmitClassName({
                  enabled: welcomeQuestion.trim().length > 0,
                })}
                aria-label="Start assistant"
                data-testid="ai-prototype-welcome-submit"
              >
                <SendHorizontal size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const promptBannerNode =
    resolvedPromptBanner && !isMobile ? (
      <div
        className={schoolShellAssistantStyleContract.promptBannerShellClassName}
        data-testid="ai-prototype-prompt-banner"
        data-ai-prototype-theme-surface="prompt-banner"
      >
        <div
          className={schoolShellAssistantStyleContract.promptBannerClassName}
        >
          {resolvedPromptBanner}
        </div>
      </div>
    ) : null;

  const bodyNode = resolvedShowWelcomeView ? (
    welcomeViewNode
  ) : (
    <div
      className={`flex min-h-0 flex-1 overflow-hidden ${shouldSplitPanel ? "flex-row" : "flex-col"}`}
      data-testid="ai-prototype-shell-body"
      data-ai-prototype-theme-surface="body"
    >
      <div
        className={`min-h-0 ${
          shouldSplitPanel
            ? `order-1 flex ${splitChatPaneClassName} flex-col overflow-hidden`
            : showChatPane
              ? "flex flex-1 flex-col overflow-hidden"
              : "hidden"
        } ${schoolShellAssistantStyleContract.chatPaneClassName}`}
        data-testid="ai-prototype-chat-pane"
        data-ai-prototype-theme-surface="chat-pane"
      >
        {promptBannerNode}
        {chatPane}
      </div>
      {shouldSplitPanel ? (
        <div
          className={schoolShellAssistantStyleContract.splitDividerClassName}
          aria-hidden="true"
          data-testid="ai-prototype-split-divider"
        />
      ) : null}
      {showSidePane ? (
        <div
          className={`min-h-0 ${shouldSplitPanel ? `order-3 flex ${splitSidePaneClassName} flex-col overflow-hidden` : "flex flex-1 flex-col overflow-hidden"} ${schoolShellAssistantStyleContract.sidePaneClassName}`}
          data-testid="ai-prototype-side-pane"
          data-ai-prototype-theme-surface="side-pane"
        >
          {utilityPaneNode}
        </div>
      ) : null}
    </div>
  );

  const authorityScopeNode = (
    <div
      className={schoolShellAssistantStyleContract.authorityScopeClassName}
      data-testid="ai-assistant-authority-scope"
      data-ai-prototype-theme-surface="authority-scope"
    >
      {headerNode}
      {bodyNode}
    </div>
  );

  return (
    <div
      className={[
        schoolShellAssistantStyleContract.shellRootClassName({
          resolvedIsFullScreen,
          useBodyOnlyFullPageChrome,
        }),
        className ?? "",
      ].join(" ")}
      data-testid="ai-prototype-shell"
      data-width-mode={resolvedWidthMode}
      data-utility-tab={resolvedUtilityTab}
      data-surface={resolvedSurface}
      data-conversation-phase={scene?.conversationPhase ?? "idle"}
      data-workspace-kind={scene?.workspace?.kind ?? "chat"}
      data-ai-prototype-theme-surface="shell"
    >
      {backdropNode}
      {showDesktopWorkspaceChrome ? (
        <div className="flex h-full min-h-0 w-full overflow-hidden">
          {fullSidebarNode}
          <div className="min-h-0 flex flex-1 flex-col overflow-hidden border-l border-border bg-surface-light">
            {authorityScopeNode}
          </div>
        </div>
      ) : (
        authorityScopeNode
      )}
    </div>
  );
}
